import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { toast } from 'react-toastify';
import config from '../config';
import ImageCropperModal from './common/ImageCropperModal';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SOURCE_SIZE = 10 * 1024 * 1024; // 10MB Source limit

const VenueGalleryManager = ({ venueId }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    
    const [images, setImages] = useState([]);
    const [limits, setLimits] = useState({ max: 0, current: 0 });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [deletingId, setDeletingId] = useState(null);

    // Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fixedAspectRatio: 3/2, // Default to 3:2 for gallery
        initialAspectRatio: 3/2,
        minWidth: 750
    });
    
    // Queue for multiple file cropping
    const [cropQueue, setCropQueue] = useState([]);
    const processingQueueRef = useRef(false);

    // Fetch gallery and limits
    const fetchGallery = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery`);
            const data = await response.json();
            
            if (response.ok) {
                // Backend now returns { images: [], limit: N }
                // Handle both new format and potential legacy format (just array)
                const imageList = Array.isArray(data) ? data : (data.images || []);
                const limitValue = !Array.isArray(data) && data.limit !== undefined ? data.limit : 0;
                
                setImages(imageList);
                setLimits({
                    max: limitValue,
                    current: imageList.length
                });
            }
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        if (venueId) {
            fetchGallery();
        }
    }, [venueId, fetchGallery]);

    // Get remaining slots
    const getRemainingSlots = () => {
        return Math.max(0, limits.max - limits.current);
    };

    // Validate files
    const validateFiles = (files) => {
        const errors = [];
        const validFiles = [];
        const remaining = getRemainingSlots() - selectedFiles.length - cropQueue.length; // Account for already selected/queued
        
        if (files.length > remaining) {
            errors.push(`You can only upload ${remaining} more image(s). Selected ${files.length}.`);
            return { validFiles: [], errors };
        }

        for (const file of files) {
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                errors.push(`${file.name}: Only JPG, PNG, and WebP are allowed`);
                continue;
            }
            if (file.size > MAX_SOURCE_SIZE) {
                errors.push(`${file.name}: File size must be less than 10MB`);
                continue;
            }
            validFiles.push(file);
        }

        return { validFiles, errors };
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const { validFiles, errors } = validateFiles(files);
        
        errors.forEach(error => toast.error(error));
        
        if (validFiles.length) {
            // Add to crop queue
            setCropQueue(prev => [...prev, ...validFiles]);
        }
        
        e.target.value = '';
    };

    // Process Crop Queue
    useEffect(() => {
        if (cropQueue.length > 0 && !cropperState.show && !processingQueueRef.current) {
            processingQueueRef.current = true;
            const file = cropQueue[0];
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                fixedAspectRatio: 3/2, // 3:2 for gallery/feed
                initialAspectRatio: 3/2,
                minWidth: 750
            });
        }
    }, [cropQueue, cropperState.show]);

    const handleCropperHide = () => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
        processingQueueRef.current = false;
        
        // If cancelled, remove current item from queue to proceed (or stop?)
        // Let's remove current item so we don't get stuck, effectively skipping it
        if (cropQueue.length > 0) {
             setCropQueue(prev => prev.slice(1));
        }
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        // Add to selected files
        setSelectedFiles(prev => [...prev, croppedFile]);
        setPreviews(prev => [...prev, { file: croppedFile, preview: previewUrl }]);
        
        // Cleanup current cropper
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
        processingQueueRef.current = false;

        // Remove processed item from queue
        setCropQueue(prev => prev.slice(1));
    };


    // Remove from selection
    const removeFromSelection = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));
    };

    // Upload images
    const handleUpload = async () => {
        if (!selectedFiles.length) return;
        
        setUploading(true);
        
        try {
            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('images', file);
            });
            
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery`, {
                method: 'POST',
                body: formData,
                headers: {}
            });
            
            const data = await response.json();
            
            if (response.ok) {
                toast.success(`${data.uploaded?.length || selectedFiles.length} image(s) uploaded successfully`);
                setSelectedFiles([]);
                setPreviews([]);
                fetchGallery();
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else {
                    toast.error(data.message || 'Failed to upload images');
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload images');
        } finally {
            setUploading(false);
        }
    };

    // Delete image
    const handleDelete = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;
        
        setDeletingId(imageId);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery/${imageId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                toast.success('Image deleted successfully');
                fetchGallery();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete image');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete image');
        } finally {
            setDeletingId(null);
        }
    };

    // Set as primary
    const handleSetPrimary = async (imageId) => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery/${imageId}/primary`, {
                method: 'PUT'
            });
            
            if (response.ok) {
                toast.success('Primary image updated');
                fetchGallery();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update primary image');
            }
        } catch (error) {
            console.error('Error setting primary:', error);
            toast.error('Failed to update primary image');
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-48">
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    return (
        <div className="card radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="text-lg fw-semibold mb-0">
                        <Icon icon="mdi:image-multiple" className="me-8" />
                        Gallery Images
                    </h6>
                    <div className="d-flex align-items-center gap-12">
                        <span className={`badge ${getRemainingSlots() > 0 ? 'bg-success-100 text-success-600' : 'bg-danger-100 text-danger-600'} px-12 py-6`}>
                            {limits.current} / {limits.max} images used
                        </span>
                        <span className={`badge ${getRemainingSlots() > 0 ? 'bg-info-100 text-info-600' : 'bg-warning-100 text-warning-600'} px-12 py-6`}>
                            {getRemainingSlots()} slots remaining
                        </span>
                    </div>
                </div>
            </div>
            
            <div className="card-body p-24">
                {/* Upload Section */}
                {limits.max === 0 ? (
                    <div className="alert alert-secondary mb-24 bg-neutral-100 border-neutral-200 text-neutral-600">
                        <div className="d-flex align-items-center">
                            <Icon icon="mdi:lock" className="text-xl me-12" />
                            <div>
                                <p className="fw-semibold mb-0">Gallery Feature Not Available</p>
                                <p className="text-sm mb-0">
                                    Your current plan does not include gallery images. 
                                    <button type="button" onClick={handleUpgrade} className="ms-4 text-primary-600 fw-medium p-0 border-0 bg-transparent text-decoration-underline">Upgrade to add this feature</button>
                                </p>
                            </div>
                        </div>
                    </div>
                ) : getRemainingSlots() > 0 ? (
                    <div className="mb-24">
                        <div className="border-2 border-dashed border-neutral-300 radius-12 p-24 text-center bg-neutral-50">
                            <input
                                type="file"
                                id="gallery-upload"
                                multiple
                                accept=".jpg,.jpeg,.png,.webp"
                                onChange={handleFileSelect}
                                className="d-none"
                            />
                            <label htmlFor="gallery-upload" className="cursor-pointer">
                                <Icon icon="mdi:cloud-upload" className="text-5xl text-primary-600 mb-12" />
                                <p className="text-lg fw-medium mb-4">Drop images here or click to upload</p>
                                <p className="text-secondary-light text-sm mb-0">
                                    Max {getRemainingSlots()} images • JPG, PNG, WebP • Max 2MB each
                                </p>
                            </label>
                        </div>
                        
                        {/* Preview selected files */}
                        {previews.length > 0 && (
                            <div className="mt-16">
                                <div className="d-flex justify-content-between align-items-center mb-12">
                                    <span className="text-sm fw-medium">Selected Files ({previews.length})</span>
                                    <button
                                        className="btn btn-primary radius-8 px-16"
                                        onClick={handleUpload}
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-8" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="mdi:upload" className="me-8" />
                                                Upload {previews.length} Image(s)
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="row g-12">
                                    {previews.map((item, index) => (
                                        <div key={index} className="col-6 col-md-4 col-lg-3">
                                            <div className="position-relative">
                                                <img
                                                    src={item.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-100 h-120-px object-fit-cover radius-8 border"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-4 p-4 radius-8"
                                                    onClick={() => removeFromSelection(index)}
                                                >
                                                    <Icon icon="mdi:close" />
                                                </button>
                                                <div className="position-absolute bottom-0 start-0 end-0 bg-black bg-opacity-50 p-4 radius-0-0-8-8">
                                                    <span className="text-white text-xs text-truncate d-block">
                                                        {item.file.name}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="alert alert-warning mb-24 d-flex align-items-center">
                        <Icon icon="mdi:alert" className="me-12 text-xl" />
                        <div>
                            You have reached the maximum number of gallery images ({limits.max}) allowed by your subscription plan.
                            <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-8 fw-semibold text-decoration-underline">Upgrade your plan</button>
                        </div>
                    </div>
                )}
                
                {/* Gallery Grid */}
                {images.length > 0 ? (
                    <div className="row g-16">
                        {images.map((image) => (
                            <div key={image.id} className="col-6 col-md-4 col-lg-3">
                                <div className="position-relative group">
                                    <img
                                        src={
                                            (image.thumbnail_url || image.image_url).startsWith('http') 
                                                ? (image.thumbnail_url || image.image_url)
                                                : `${config.API_BASE_URL}/${image.thumbnail_url || image.image_url}`
                                        }
                                        alt={image.alt_text || 'Gallery image'}
                                        className="w-100 h-160-px object-fit-cover radius-8 border"
                                    />
                                    
                                    {/* Primary Badge */}
                                    {image.is_primary && (
                                        <span className="position-absolute top-0 start-0 m-8 badge bg-primary-600 px-8 py-4">
                                            <Icon icon="mdi:star" className="me-4" />
                                            Primary
                                        </span>
                                    )}
                                    
                                    {/* Actions Overlay */}
                                    <div className="position-absolute top-0 end-0 m-8 d-flex gap-4">
                                        {!image.is_primary && (
                                            <button
                                                type="button"
                                                className="bg-white shadow-sm radius-8 d-flex align-items-center justify-content-center w-32-px h-32-px border-0"
                                                onClick={() => handleSetPrimary(image.id)}
                                                title="Set as primary"
                                            >
                                                <Icon icon="mdi:star-outline" className="text-warning-600 text-lg" />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="bg-white shadow-sm radius-8 d-flex align-items-center justify-content-center w-32-px h-32-px border-0"
                                            onClick={() => handleDelete(image.id)}
                                            disabled={deletingId === image.id}
                                            title="Delete"
                                        >
                                            {deletingId === image.id ? (
                                                <span className="spinner-border spinner-border-sm text-danger" />
                                            ) : (
                                                <Icon icon="mdi:delete" className="text-danger-600 text-lg" />
                                            )}
                                        </button>
                                    </div>
                                    
                                    {/* Image Info */}
                                    <div className="position-absolute bottom-0 start-0 end-0 bg-gradient-dark p-8 radius-0-0-8-8">
                                        <p className="text-white text-xs mb-0">
                                            {new Date(image.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : limits.max > 0 ? (
                    <div className="text-center py-48 bg-neutral-50 radius-12">
                        <Icon icon="mdi:image-off" className="text-5xl text-secondary-light mb-12" />
                        <p className="text-secondary-light mb-0">No images in gallery yet</p>
                    </div>
                ) : null}

                {/* Image Cropper Modal */}
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            </div>
        </div>
    );
};

export default VenueGalleryManager;
